import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {cn} from '@/lib/utils'

export type SeveridadAviso = 'red' | 'amber' | 'neutral'

export type AvisoDePagina = {
  id: string
  severidad: SeveridadAviso
  nodo: ReactNode
}

/** La entrada falsa es parte del contrato: las páginas arman la lista con `condicion && {...}`. */
export type EntradaAvisoDePagina = AvisoDePagina | false | null | undefined

const RANGO: Record<SeveridadAviso, number> = {red: 0, amber: 1, neutral: 2}

const PUNTO: Record<SeveridadAviso, string> = {
  red: 'bg-tone-red-fg',
  amber: 'bg-tone-amber-fg',
  neutral: 'bg-muted-foreground',
}

/** Dos avisos caben sin empujar el trabajo fuera de la pantalla; tres ya no. */
const TOPE_SIN_PLEGAR = 2

/** Estado persistente que la ley exige ver: a diferencia de la burbuja (`components/avisos-region.tsx`)
 * nunca se vuelve efímero ni se auto-oculta. */
export function PageNotices({items}: {items: readonly EntradaAvisoDePagina[]}) {
  const {t} = useTranslation()
  const [expandido, setExpandido] = useState(false)
  const presentes = items.filter((item): item is AvisoDePagina => !!item)
  // `sort` estable: dentro de una severidad se respeta el orden en que la página los declaró.
  const ordenados = [...presentes].sort((a, b) => RANGO[a.severidad] - RANGO[b.severidad])

  if (ordenados.length === 0) return null

  const pliega = ordenados.length > TOPE_SIN_PLEGAR && !expandido
  const visibles = pliega ? ordenados.slice(0, 1) : ordenados
  const plegados = ordenados.slice(visibles.length)

  return (
    <div data-testid="page-notices" className="flex flex-col gap-3">
      {visibles.map((aviso) => (
        <div key={aviso.id}>{aviso.nodo}</div>
      ))}
      {plegados.length > 0 && (
        <button
          type="button"
          data-testid="page-notices-mas"
          aria-expanded={false}
          onClick={() => setExpandido(true)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-left text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
          <span aria-hidden="true" className="flex shrink-0 gap-1">
            {plegados.map((aviso) => (
              <span key={aviso.id} className={cn('size-2 rounded-full', PUNTO[aviso.severidad])} />
            ))}
          </span>
          {t('avisos.pagina.mas', {count: plegados.length})}
        </button>
      )}
      {expandido && ordenados.length > TOPE_SIN_PLEGAR && (
        <button
          type="button"
          data-testid="page-notices-menos"
          aria-expanded
          onClick={() => setExpandido(false)}
          className="w-fit cursor-pointer text-sm font-medium text-primary hover:underline">
          {t('avisos.pagina.menos')}
        </button>
      )}
    </div>
  )
}
